import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../app/store/store';
import {
  fetchWorkShifts,
  createWorkShift,
  updateWorkShift,
  deleteWorkShift,
} from '../../../entities/helpdesk-settings/model';
import { helpdeskSettingsApi } from '../../../api/services/helpdesk-settings.service';
import type { WorkShift, ShiftInterval } from '../../../types/helpdeskSettingsInterfaces';
import { modalCls } from '@shared/ui/form-modal';

/*
 * Рабочие смены. По обратной связи руководителя ТП:
 *  — смена состоит из нескольких окон «дни + часы» (будни 9–18, сб 10–14,
 *    вс 23:00–01:00), а не из одного; окно через полночь задаётся одной
 *    строкой и относится к дню начала;
 *  — порядок в списке — ручной (drag&drop за «ручку»);
 *  — несколько смен можно выделить и удалить разом;
 *  — шапка блока с кнопкой «Добавить смену» прилипает к верху, вторая
 *    кнопка — под списком, чтобы не скроллить длинный список.
 */

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const WEEKDAYS_FULL = [
  'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье',
];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];
const WORK_DAYS = [0, 1, 2, 3, 4];
const WEEKEND = [5, 6];

const sameSet = (a: number[], b: number[]) =>
  a.length === b.length && a.slice().sort().every((v, i) => v === b.slice().sort()[i]);

/** «Пн–Пт», «Сб, Вс», «Любой день» */
const daysLabel = (days: number[]): string => {
  const d = (days || []).slice().sort((a, b) => a - b);
  if (d.length === 0 || d.length === 7) return 'Любой день';
  if (sameSet(d, WORK_DAYS)) return 'Пн–Пт';
  if (sameSet(d, WEEKEND)) return 'Сб, Вс';
  return d.map((x) => WEEKDAYS[x]).join(', ');
};

const hhmm = (s: string) => (s || '').slice(0, 5);
const isOvernight = (w: ShiftInterval) => hhmm(w.end) <= hhmm(w.start);

const intervalLabel = (w: ShiftInterval): string =>
  `${daysLabel(w.weekdays)} ${hhmm(w.start)}–${hhmm(w.end)}${isOvernight(w) ? ' (след. день)' : ''}`;

const shiftSummary = (s: WorkShift): string =>
  (s.intervals || []).map(intervalLabel).join(' · ') || 'без окон';

/* ── Модалка ── */

interface ShiftModalProps {
  shift?: WorkShift | null;
  onClose: () => void;
  onSave: (data: Partial<WorkShift>) => void;
  onDelete?: () => void;
}

const inputCls =
  'px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400';

const ShiftModal: React.FC<ShiftModalProps> = ({ shift, onClose, onSave, onDelete }) => {
  const [name, setName] = useState(shift?.name || '');
  const [intervals, setIntervals] = useState<ShiftInterval[]>(
    shift?.intervals?.length
      ? shift.intervals.map((w) => ({ ...w, start: hhmm(w.start), end: hhmm(w.end) }))
      : [{ weekdays: WORK_DAYS, start: '09:00', end: '18:00' }],
  );
  const [operatorIds, setOperatorIds] = useState<number[]>(
    shift?.operators?.map((o) => o.id) || [],
  );
  const [operators, setOperators] = useState<
    { id: number; username: string; first_name: string; last_name: string; full_name: string }[]
  >([]);
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    helpdeskSettingsApi
      .getAvailableOperators()
      .then((r) => setOperators(r.data))
      .catch(() => setOperators([]));
  }, []);

  useEffect(() => {
    if (!addOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (addRef.current && !addRef.current.contains(e.target as Node)) setAddOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [addOpen]);

  const patchInterval = (i: number, patch: Partial<ShiftInterval>) =>
    setIntervals((list) => list.map((w, idx) => (idx === i ? { ...w, ...patch } : w)));
  const removeInterval = (i: number) =>
    setIntervals((list) => list.filter((_, idx) => idx !== i));
  const toggleDay = (i: number, d: number) =>
    setIntervals((list) =>
      list.map((w, idx) =>
        idx === i
          ? {
              ...w,
              weekdays: w.weekdays.includes(d)
                ? w.weekdays.filter((x) => x !== d)
                : [...w.weekdays, d].sort((a, b) => a - b),
            }
          : w,
      ),
    );

  const addInterval = (days: number[]) => {
    const last = intervals[intervals.length - 1];
    setIntervals((list) => [
      ...list,
      { weekdays: days, start: last?.start || '09:00', end: last?.end || '18:00' },
    ]);
    setAddOpen(false);
  };

  const toggleOp = (id: number) =>
    setOperatorIds((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));

  const opName = (o: {
    first_name: string;
    last_name: string;
    full_name: string;
    username: string;
  }) => o.full_name || `${o.last_name} ${o.first_name}`.trim() || o.username;

  const valid =
    name.trim().length > 0 &&
    intervals.length > 0 &&
    intervals.every((w) => w.start && w.end && w.start !== w.end);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onSave({
      name: name.trim(),
      intervals: intervals.map((w) => ({
        weekdays: w.weekdays.slice().sort((a, b) => a - b),
        start: hhmm(w.start),
        end: hhmm(w.end),
      })),
      operator_ids: operatorIds,
    });
  };

  const addOptions: { label: string; days: number[] }[] = [
    { label: 'Все дни', days: [] },
    { label: 'Рабочие дни (Пн–Пт)', days: WORK_DAYS },
    { label: 'Выходные (Сб, Вс)', days: WEEKEND },
    ...WEEKDAYS_FULL.map((label, i) => ({ label, days: [i] })),
  ];

  return (
    <div className={modalCls.overlay}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`${modalCls.panel} max-w-lg`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            {shift ? 'Изменить рабочую смену' : 'Добавить рабочую смену'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className={modalCls.body}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название <span className="text-red-500">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Напр.: Дневная смена"
              className={`w-full ${inputCls}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Дни и время</label>
            <div className="space-y-2">
              {intervals.map((w, i) => (
                <div key={i} className="border border-gray-200 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex gap-1">
                      {WEEKDAYS.map((d, di) => (
                        <button
                          key={di}
                          type="button"
                          title={WEEKDAYS_FULL[di]}
                          onClick={() => toggleDay(i, di)}
                          className={`w-8 h-8 rounded-md text-xs font-medium border transition-colors ${
                            w.weekdays.includes(di)
                              ? 'bg-[#e3282a] text-white border-[#e3282a]'
                              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <span className="text-xs text-gray-500">с</span>
                      <input
                        type="time"
                        value={w.start}
                        onChange={(e) => patchInterval(i, { start: e.target.value })}
                        className={`${inputCls} w-[108px] py-1`}
                      />
                      <span className="text-xs text-gray-500">по</span>
                      <input
                        type="time"
                        value={w.end}
                        onChange={(e) => patchInterval(i, { end: e.target.value })}
                        className={`${inputCls} w-[108px] py-1`}
                      />
                      <button
                        type="button"
                        onClick={() => removeInterval(i)}
                        disabled={intervals.length === 1}
                        title="Убрать окно"
                        className="w-7 h-7 rounded-md text-gray-400 hover:text-[#e3282a] hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">
                    {w.weekdays.length === 0 ? 'Любой день' : daysLabel(w.weekdays)}
                    {w.start && w.end && w.start === w.end && (
                      <span className="text-[#e3282a]"> · начало и конец совпадают</span>
                    )}
                    {w.start && w.end && isOvernight(w) && w.start !== w.end && (
                      <span> · до {hhmm(w.end)} следующего дня</span>
                    )}
                  </p>
                </div>
              ))}
            </div>

            <div className="relative inline-block mt-2" ref={addRef}>
              <button
                type="button"
                onClick={() => setAddOpen((v) => !v)}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                + Добавить окно ▾
              </button>
              {addOpen && (
                <div className="absolute left-0 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 max-h-64 overflow-y-auto">
                  {addOptions.map((o) => (
                    <button
                      key={o.label}
                      type="button"
                      onClick={() => addInterval(o.days)}
                      className="block w-full text-left px-3 py-1.5 text-sm text-[#2d2c2c] hover:bg-gray-50"
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Окно через полночь задаётся одной строкой: Вс 23:00–01:00 — это вечер воскресенья и ночь на понедельник.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Операторы в смене</label>
            <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg divide-y divide-gray-100">
              {operators.length === 0 ? (
                <p className="text-xs text-gray-400 px-3 py-3">Нет доступных операторов</p>
              ) : (
                operators.map((o) => (
                  <label
                    key={o.id}
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={operatorIds.includes(o.id)}
                      onChange={() => toggleOp(o.id)}
                      className="w-4 h-4 rounded border-gray-300 focus:ring-red-400"
                    />
                    <span className="text-sm text-[#2d2c2c]">{opName(o)}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className={`flex items-center justify-between ${modalCls.footerSticky}`}>
            <div>
              {shift && onDelete && (
                <button
                  type="button"
                  onClick={onDelete}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800"
                >
                  Удалить
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={!valid}
                className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] disabled:opacity-50"
              >
                {shift ? 'Сохранить' : 'Добавить'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Список ── */

const WorkShiftSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { workShifts, workShiftsStatus } = useSelector(
    (state: RootState) => state.helpdeskSettings,
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WorkShift | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Локальный порядок во время перетаскивания; после сохранения — из стора.
  const [order, setOrder] = useState<number[] | null>(null);
  const dragId = useRef<number | null>(null);
  const [overId, setOverId] = useState<number | null>(null);

  useEffect(() => {
    if (workShiftsStatus === 'idle') dispatch(fetchWorkShifts());
  }, [dispatch, workShiftsStatus]);

  const list = useMemo(() => {
    if (!order) return workShifts;
    const byId = new Map(workShifts.map((s) => [s.id, s]));
    const ordered = order.map((id) => byId.get(id)).filter(Boolean) as WorkShift[];
    const rest = workShifts.filter((s) => !order.includes(s.id));
    return [...ordered, ...rest];
  }, [workShifts, order]);

  const handleSave = useCallback(
    async (data: Partial<WorkShift>) => {
      if (editing) await dispatch(updateWorkShift({ id: editing.id, data }));
      else await dispatch(createWorkShift(data));
      dispatch(fetchWorkShifts());
      setModalOpen(false);
      setEditing(null);
    },
    [dispatch, editing],
  );

  const handleDelete = useCallback(async () => {
    if (!editing) return;
    await dispatch(deleteWorkShift(editing.id));
    setSelected((l) => l.filter((x) => x !== editing.id));
    setModalOpen(false);
    setEditing(null);
  }, [dispatch, editing]);

  const toggleSelected = (id: number) =>
    setSelected((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const allSelected = list.length > 0 && selected.length === list.length;
  const toggleAll = () => setSelected(allSelected ? [] : list.map((s) => s.id));

  const handleBulkDelete = async () => {
    if (!confirmBulk) {
      setConfirmBulk(true);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await helpdeskSettingsApi.bulkDeleteWorkShifts(selected);
      setSelected([]);
      setOrder(null);
      await dispatch(fetchWorkShifts());
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Не удалось удалить смены.');
    } finally {
      setBusy(false);
      setConfirmBulk(false);
    }
  };

  /* drag&drop (нативный HTML5, без библиотек) */
  const onDragStart = (id: number) => (e: React.DragEvent) => {
    dragId.current = id;
    e.dataTransfer.effectAllowed = 'move';
    setOrder(list.map((s) => s.id));
  };
  const onDragOver = (id: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const from = dragId.current;
    if (from == null || from === id) return;
    setOverId(id);
    setOrder((cur) => {
      const ids = (cur || list.map((s) => s.id)).slice();
      const fi = ids.indexOf(from);
      const ti = ids.indexOf(id);
      if (fi < 0 || ti < 0 || fi === ti) return cur;
      ids.splice(fi, 1);
      ids.splice(ti, 0, from);
      return ids;
    });
  };
  const onDragEnd = async () => {
    const ids = order;
    dragId.current = null;
    setOverId(null);
    if (!ids) return;
    const current = workShifts.map((s) => s.id);
    if (ids.join(',') === current.join(',')) {
      setOrder(null);
      return;
    }
    setBusy(true);
    setError('');
    try {
      await helpdeskSettingsApi.reorderWorkShifts(ids);
      await dispatch(fetchWorkShifts());
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Не удалось сохранить порядок.');
    } finally {
      setOrder(null);
      setBusy(false);
    }
  };

  const addButton = (
    <button
      onClick={() => {
        setEditing(null);
        setModalOpen(true);
      }}
      className="px-4 py-2 text-sm font-medium text-white bg-[#e3282a] rounded-lg hover:bg-[#c82022] transition-colors whitespace-nowrap"
    >
      + Добавить смену
    </button>
  );

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      {/* Шапка прилипает к верху при прокрутке длинного списка */}
      <div className="sticky top-0 z-10 bg-white -mx-6 px-6 pt-1 pb-3 border-b border-gray-100 mb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-[#2d2c2c]">Рабочие смены</h2>
            <p className="text-sm text-gray-400">
              Именованная смена: окна «дни + часы» и операторы. Используется в маршрутизации и SLA.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selected.length > 0 && (
              <button
                type="button"
                onClick={handleBulkDelete}
                onBlur={() => setConfirmBulk(false)}
                disabled={busy}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  confirmBulk
                    ? 'text-white bg-[#e3282a] hover:bg-[#c82022]'
                    : 'text-[#e3282a] bg-red-50 hover:bg-red-100'
                }`}
              >
                {confirmBulk
                  ? `Точно удалить ${selected.length}?`
                  : `Удалить выбранные (${selected.length})`}
              </button>
            )}
            {addButton}
          </div>
        </div>
      </div>

      {error && <p className="mb-3 text-xs text-red-500">{error}</p>}

      {list.length > 0 ? (
        <div>
          <label className="flex items-center gap-2 px-2 py-1.5 text-xs text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="w-4 h-4 rounded border-gray-300 focus:ring-red-400"
            />
            Выбрать все · перетаскивайте за ⋮⋮, чтобы изменить порядок
          </label>
          {list.map((s) => (
            <div
              key={s.id}
              draggable={!busy}
              onDragStart={onDragStart(s.id)}
              onDragOver={onDragOver(s.id)}
              onDragEnd={onDragEnd}
              onDrop={(e) => e.preventDefault()}
              className={`flex items-center gap-2 py-3 border-b border-gray-100 last:border-b-0 -mx-2 px-2 rounded transition-colors ${
                overId === s.id ? 'bg-red-50' : 'hover:bg-gray-50'
              } ${selected.includes(s.id) ? 'bg-gray-50' : ''}`}
            >
              <span
                className="text-gray-300 cursor-grab active:cursor-grabbing select-none px-1 text-base leading-none"
                title="Перетащить"
              >
                ⋮⋮
              </span>
              <input
                type="checkbox"
                checked={selected.includes(s.id)}
                onChange={() => toggleSelected(s.id)}
                onClick={(e) => e.stopPropagation()}
                className="w-4 h-4 rounded border-gray-300 focus:ring-red-400"
              />
              <div
                className="min-w-0 flex-1 cursor-pointer"
                onClick={() => {
                  setEditing(s);
                  setModalOpen(true);
                }}
              >
                <p className="text-sm font-medium text-[#2d2c2c]">{s.name}</p>
                <p className="text-xs text-gray-400 truncate">
                  {shiftSummary(s)} ·{' '}
                  {s.operators?.length
                    ? `${s.operators.length} операт.`
                    : 'без операторов'}
                </p>
              </div>
              <span
                className={`text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 ml-2 ${
                  s.is_active
                    ? 'bg-green-50 text-green-600 border border-green-200'
                    : 'bg-gray-50 text-gray-500 border border-gray-200'
                }`}
              >
                {s.is_active ? 'Активна' : 'Неактивна'}
              </span>
            </div>
          ))}
          {list.length > 6 && <div className="flex justify-end pt-4">{addButton}</div>}
        </div>
      ) : (
        <p className="text-sm text-gray-400 text-center py-8">
          Рабочих смен пока нет. Нажмите «Добавить смену».
        </p>
      )}

      {modalOpen && (
        <ShiftModal
          shift={editing}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSave={handleSave}
          onDelete={editing ? handleDelete : undefined}
        />
      )}
    </div>
  );
};

export default WorkShiftSettings;
